import React from "react";
import { useSelector } from "react-redux";
import {
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ScatterChart,
  Scatter,
} from "recharts";
import { RootState } from "../../../redux/store";
import { getAge } from "./ageUtils";
import { Box, Grid, Paper, Typography } from "@mui/material";

// Define the Client interface (could also be imported from partnerClients.ts)
interface Client {
  clientUserId: string;
  birthday: Date;
  gender: string;
  name: string;
  weight: number;
  activityLevel: string;
  height: number;
}

// Helper to bin ages into 5-year intervals (e.g. 0-4, 5-9, etc.)
function getAgeRange(age: number): string {
  const start = Math.floor(age / 5) * 5;
  const end = start + 4;
  return `${start}-${end}`;
}

const Reports: React.FC = () => {
  const partnerClients =
    useSelector((state: RootState) => state.partners.clientsData) || [];
  console.log(partnerClients);

  // Total clients count
  const totalClients = partnerClients.length;

  // Calculate gender distribution
  type GenderMap = { male: number; female: number };
  const genderMap = partnerClients.reduce<GenderMap>(
    (acc, client: Client) => {
      const g = client.gender?.toLowerCase();
      if (g === "male") {
        acc.male += 1;
      } else if (g === "female") {
        acc.female += 1;
      }
      return acc;
    },
    { male: 0, female: 0 }
  );

  // Prepare data for the Gender PieChart
  const genderData = [
    { name: "Male", value: genderMap.male },
    { name: "Female", value: genderMap.female },
  ];

  // Calculate age distribution by 5-year intervals
  const ageMap = partnerClients.reduce<Record<string, number>>(
    (acc, client: Client) => {
      const age = getAge(new Date(client.birthday).toISOString());
      const range = age >= 0 ? getAgeRange(age) : "N/A";
      if (!acc[range]) {
        acc[range] = 0;
      }
      acc[range] += 1;
      return acc;
    },
    {}
  );
  const ageData = Object.entries(ageMap).map(([range, count]) => ({
    range,
    count,
  }));

  // Weight Distribution Data (using fixed bins)
  const weightDataObj = partnerClients.reduce<Record<string, number>>(
    (acc, client: Client) => {
      const weight = client.weight;
      let bin = "";
      if (weight < 50) {
        bin = "<50";
      } else if (weight < 60) {
        bin = "50-59";
      } else if (weight < 70) {
        bin = "60-69";
      } else if (weight < 80) {
        bin = "70-79";
      } else if (weight < 90) {
        bin = "80-89";
      } else {
        bin = "90+";
      }
      acc[bin] = (acc[bin] || 0) + 1;
      return acc;
    },
    {}
  );
  const weightData = Object.entries(weightDataObj).map(([bin, count]) => ({
    bin,
    count,
  }));

  // Height Distribution Data (using fixed bins)
  const heightDataObj = partnerClients.reduce<Record<string, number>>(
    (acc, client: Client) => {
      const height = client.height;
      let bin = "";
      if (height < 150) {
        bin = "<150";
      } else if (height < 160) {
        bin = "150-159";
      } else if (height < 170) {
        bin = "160-169";
      } else if (height < 180) {
        bin = "170-179";
      } else if (height < 190) {
        bin = "180-189";
      } else {
        bin = "190+";
      }
      acc[bin] = (acc[bin] || 0) + 1;
      return acc;
    },
    {}
  );
  const heightData = Object.entries(heightDataObj).map(([bin, count]) => ({
    bin,
    count,
  }));

  // BMI Distribution Data
  const bmiDataObj = partnerClients.reduce<Record<string, number>>(
    (acc, client: Client) => {
      // Assuming height is in cm, convert to meters
      const heightInMeters = client.height / 100;
      const bmi = client.weight / (heightInMeters * heightInMeters);
      let category = "";
      if (bmi < 18.5) {
        category = "Underweight";
      } else if (bmi < 25) {
        category = "Normal";
      } else if (bmi < 30) {
        category = "Overweight";
      } else {
        category = "Obese";
      }
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    {}
  );
  const bmiData = Object.entries(bmiDataObj).map(([category, count]) => ({
    category,
    count,
  }));

  // Activity Level Breakdown Data
  const activityDataObj = partnerClients.reduce<Record<string, number>>(
    (acc, client: Client) => {
      const level = client.activityLevel;
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    },
    {}
  );
  const activityData = Object.entries(activityDataObj).map(
    ([level, count]) => ({ level, count })
  );

  // Scatter Data: Height vs. Weight
  const scatterData = partnerClients.map((client: Client) => ({
    weight: client.weight,
    height: client.height,
  }));

  // Stacked Bar Chart Data: Gender breakdown by Activity Level
  const stackedDataObj = partnerClients.reduce<
    Record<string, { activityLevel: string; male: number; female: number }>
  >((acc, client: Client) => {
    const level = client.activityLevel;
    const gender = client.gender?.toLowerCase();
    if (!acc[level]) {
      acc[level] = { activityLevel: level, male: 0, female: 0 };
    }
    if (gender === "male") {
      acc[level].male += 1;
    } else if (gender === "female") {
      acc[level].female += 1;
    }
    return acc;
  }, {});
  const stackedData = Object.values(stackedDataObj);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2, overflowX: "auto" }}>
      <Grid container spacing={2}>
        {/* Total Clients */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              textAlign: "center",
              height: 300,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6">Total Clients</Typography>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {totalClients}
            </Typography>
          </Paper>
        </Grid>

        {/* Gender Distribution Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, textAlign: "center", height: 300 }}>
            <Typography variant="h6">Gender Distribution</Typography>
            <Box sx={{ width: "100%", height: "100%", mt: 1 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    dataKey="value"
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#132923"
                    label
                  />
                  <Tooltip />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    height={36}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Age Distribution as Horizontal Bar Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Age Distribution (5-year bins)
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <BarChart
                  data={ageData}
                  layout="vertical"
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="range" type="category" />
                  <Tooltip />
                  <Legend layout="horizontal" />
                  <Bar dataKey="count" fill="#132923" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Weight Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Weight Distribution
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <BarChart
                  data={weightData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bin" type="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend layout="horizontal" />
                  <Bar dataKey="count" fill="#132923" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Height Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Height Distribution
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <BarChart
                  data={heightData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bin" type="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend layout="horizontal" />
                  <Bar dataKey="count" fill="#132923" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* BMI Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              BMI Distribution
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <BarChart
                  data={bmiData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" type="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend layout="horizontal" />
                  <Bar dataKey="count" fill="#132923" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Activity Level Breakdown Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300, textAlign: "center" }}>
            <Typography variant="h6">Activity Level Breakdown</Typography>
            <Box sx={{ width: "100%", height: "100%", mt: 1 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    dataKey="count"
                    data={activityData}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#132923"
                    label
                  />
                  <Tooltip />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    height={36}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Scatter Plot: Height vs. Weight */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Height vs. Weight
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <ScatterChart
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="weight" type="number" name="Weight" />
                  <YAxis dataKey="height" type="number" name="Height" />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                  <Scatter data={scatterData} fill="#132923" />
                </ScatterChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Stacked Bar Chart: Gender by Activity Level */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 300 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Gender by Activity Level
            </Typography>
            <Box sx={{ width: "100%", height: "100%" }}>
              <ResponsiveContainer>
                <BarChart
                  data={stackedData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="activityLevel" type="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend layout="horizontal" />
                  <Bar dataKey="male" stackId="a" fill="#132923" />
                  <Bar dataKey="female" stackId="a" fill="#132923" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
